import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMinuteNow } from '@hooks/useMinuteNow';
import type { RouteMonitoringJoinResult } from '../hooks/useRouteMonitoringJoin';
import { LazyRoutePlanningMap } from '../map/LazyRoutePlanningMap';
import type { RoutePlanningStop } from '../map/RoutePlanningMap';
import { useDriverMapData } from './driverMapModel';
import type { DriverStops } from './driverStopsModel';
import { StopLine } from './StopLine';

interface PhoneDriverMapProps {
  /** The Onfleet worker id the map and the join know the driver by. */
  workerId: string;
  /** The day's full `/api/route-planning/stops` answer, as the list holds it. */
  stops: RoutePlanningStop[];
  monitoringJoin: RouteMonitoringJoinResult;
  rows: Pick<DriverStops, 'pending' | 'done'>;
}

/**
 * One driver's route on the phone (#2679): the map draws this driver only,
 * and the card below it shows one row of the driver list. A tap on a stop
 * switches the card to that job; the card opens the job like the list row.
 * The card sits below the map, not on it, so the map legend stays visible.
 * No `focusedStop`: the card is the selection, a popup would repeat it.
 */
export function PhoneDriverMap({ workerId, stops, monitoringJoin, rows }: PhoneDriverMapProps) {
  const { t } = useTranslation();
  const nowMs = useMinuteNow();
  const data = useDriverMapData(stops, monitoringJoin, workerId);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(
    () => rows.pending[0]?.jobIds[0] ?? null,
  );

  if (data.stops.length === 0 && data.join.driverLocations.length === 0) {
    return (
      <div data-testid="phone-driver-map">
        <p className="m-0 text-muted-foreground">{t('dispatch.phone.stopsView.mapEmpty')}</p>
      </div>
    );
  }

  const row =
    selectedJobId === null
      ? undefined
      : [...rows.pending, ...rows.done].find((r) => r.jobIds.includes(selectedJobId));

  return (
    <div data-testid="phone-driver-map" className="flex flex-col gap-3">
      <div className="h-[60dvh]">
        <LazyRoutePlanningMap
          stops={data.stops}
          monitoringJoin={data.join}
          selectedDriverId={workerId}
          onStopSelect={setSelectedJobId}
        />
      </div>
      {row && (
        <div
          data-testid="phone-driver-map-card"
          className="overflow-hidden rounded-xl bg-card outline outline-1 -outline-offset-1 outline-border/50"
        >
          <StopLine row={row} nowMs={nowMs} />
        </div>
      )}
    </div>
  );
}
